import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  FolderLock, Plus, Trash, Eye, MagnifyingGlass, DownloadSimple,
  FileDoc, FilePdf, ImageSquare, File, Warning, CheckCircle, Clock, Tag, X
} from '@phosphor-icons/react';

const CATEGORIES = ['Identity', 'Property', 'Insurance', 'Warranty', 'Financial', 'Medical', 'Legal', 'Other'];

const FILE_ICONS = {
  pdf: FilePdf,
  jpg: ImageSquare,
  jpeg: ImageSquare,
  png: ImageSquare,
  doc: FileDoc,
  file: File,
};

const STATUS_STYLES = {
  expired: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Expired', icon: Warning },
  expiring_soon: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Expiring Soon', icon: Clock },
  valid: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Valid', icon: CheckCircle },
};

export default function DocumentsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [form, setForm] = useState({ name: '', category: 'Identity', file_data: '', file_type: '', expiry_date: '', notes: '', tags: '' });
  const [uploading, setUploading] = useState(false);

  const fetchDocs = async () => {
    try {
      const params = {};
      if (catFilter) params.category = catFilter;
      if (search) params.search = search;
      const res = await api.get('/documents', { params });
      setDocs(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchDocs(); }, [api, catFilter, search]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File too large. Maximum 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const ext = file.name.split('.').pop().toLowerCase();
      setForm(f => ({ ...f, file_data: reader.result, file_type: ext }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file_data) return;
    setUploading(true);
    try {
      await api.post('/documents', form);
      setShowForm(false);
      setForm({ name: '', category: 'Identity', file_data: '', file_type: '', expiry_date: '', notes: '', tags: '' });
      fetchDocs();
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/documents/${id}`); fetchDocs(); } catch (e) { console.error(e); }
  };

  const handleView = async (id) => {
    try {
      const res = await api.get(`/documents/${id}`);
      setViewDoc(res.data);
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const FileIcon = (type) => FILE_ICONS[type] || File;

  return (
    <div data-testid="documents-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Security</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('document_vault')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Store and manage important household documents</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="upload-document-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('upload_document')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 relative z-10">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search documents..." data-testid="doc-search-input"
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setCatFilter('')}
            className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${!catFilter ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
            All
          </button>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setCatFilter(cat)} data-testid={`doc-filter-${cat.toLowerCase()}`}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${catFilter === cat ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {docs.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <FolderLock size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No documents found</p>
          </div>
        ) : docs.map(doc => {
          const Icon = FileIcon(doc.file_type);
          const status = STATUS_STYLES[doc.expiry_status];
          return (
            <div key={doc.doc_id} className="glass-card p-6 animate-fade-up" data-testid={`doc-card-${doc.doc_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center">
                    <Icon size={20} className="text-[#FF6B35]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{doc.name}</p>
                    <p className="text-[11px] text-neutral-500">{doc.category} &middot; {doc.file_type?.toUpperCase()}</p>
                  </div>
                </div>
                {status && (
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${status.bg} ${status.text} flex items-center gap-1`}>
                    <status.icon size={10} /> {status.label}
                  </span>
                )}
              </div>

              {doc.expiry_date && (
                <p className="text-[11px] text-neutral-500 mb-2">Expires: {doc.expiry_date}</p>
              )}

              {doc.tags && (
                <div className="flex items-center gap-1 mb-3 flex-wrap">
                  <Tag size={11} className="text-neutral-600" />
                  {doc.tags.split(',').map((tag, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-neutral-400">{tag.trim()}</span>
                  ))}
                </div>
              )}

              {doc.notes && (
                <p className="text-xs text-neutral-500 mb-3 line-clamp-2">{doc.notes}</p>
              )}

              <div className="flex gap-2 mt-auto">
                <button onClick={() => handleView(doc.doc_id)} data-testid={`view-doc-${doc.doc_id}`}
                  className="flex-1 py-2 rounded-full border border-white/10 text-xs font-medium text-neutral-300 hover:bg-white/5 flex items-center justify-center gap-1">
                  <Eye size={14} /> View
                </button>
                <button onClick={() => handleDelete(doc.doc_id)} data-testid={`delete-doc-${doc.doc_id}`}
                  className="py-2 px-3 rounded-full border border-white/10 text-xs text-neutral-500 hover:text-red-400 hover:border-red-400/20 transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* View Document Modal */}
      {viewDoc && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setViewDoc(null)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-heading font-semibold text-white text-lg">{viewDoc.name}</h3>
                <p className="text-xs text-neutral-500 mt-1">{viewDoc.category} &middot; Uploaded {new Date(viewDoc.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setViewDoc(null)} className="p-2 rounded-xl hover:bg-white/5 text-neutral-500">
                <X size={18} />
              </button>
            </div>

            {viewDoc.file_data && (
              <div className="rounded-2xl overflow-hidden border border-white/10 mb-4 bg-white/5">
                {viewDoc.file_type === 'pdf' ? (
                  <iframe src={viewDoc.file_data} className="w-full h-[500px]" title={viewDoc.name} />
                ) : ['jpg', 'jpeg', 'png'].includes(viewDoc.file_type) ? (
                  <img src={viewDoc.file_data} alt={viewDoc.name} className="w-full max-h-[500px] object-contain" />
                ) : (
                  <div className="p-10 text-center">
                    <File size={48} weight="duotone" className="mx-auto text-neutral-500 mb-3" />
                    <p className="text-sm text-neutral-400">Preview not available for .{viewDoc.file_type} files</p>
                  </div>
                )}
              </div>
            )}

            {viewDoc.file_data && (
              <a href={viewDoc.file_data} download={`${viewDoc.name}.${viewDoc.file_type}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">
                <DownloadSimple size={16} /> Download
              </a>
            )}
          </div>
        </div>
      )}

      {/* Upload Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{t('upload_document')}</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('name')}</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  data-testid="doc-name-input" placeholder="e.g., Aadhaar Card"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('category')}</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  data-testid="doc-category-select"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">File (Max 5MB)</label>
                <input type="file" onChange={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  data-testid="doc-file-input"
                  className="w-full text-sm text-neutral-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border file:border-white/10 file:bg-white/5 file:text-neutral-300 file:text-xs file:font-medium hover:file:bg-white/10" />
                {form.file_data && <p className="text-xs text-emerald-400 mt-1">File selected ({form.file_type})</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('expiry_date')}</label>
                  <input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                    data-testid="doc-expiry-input"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">{t('tags')}</label>
                  <input type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                    data-testid="doc-tags-input" placeholder="id, passport"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  data-testid="doc-notes-input" placeholder="Optional notes..."
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" disabled={!form.file_data || uploading} data-testid="save-document-btn"
                  className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow disabled:opacity-40">
                  {uploading ? 'Uploading...' : t('upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
